import React from "react";
import styled, { css, keyframes } from "styled-components";
import { Loading03Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";

/**
 * The one loading indicator in the product.
 *
 * There were four. `components/Loading` drew this mark but through antd's
 * `Spin`, which brings its own wrapper, its own sizes and its own four-dot
 * fallback; `components/LoadingIndicator` drew a hand-built SVG arc that grew
 * and shrank as it turned; `components/Button` swapped its label for
 * FontAwesome's `faSpinner`, a solid six-spoke mark in an app whose icons are
 * all stroked; and three pages reached for antd `Spin` directly. So a
 * customer waiting for a page, waiting for a table and waiting for a button
 * they had just pressed was shown three different marks turning at three
 * different rates.
 *
 * This is the mark: Hugeicons' twelve-spoke `Loading03Icon`, stroked like
 * every other glyph, turning once a second. It takes `currentColor`, so it
 * is white on a filled button and ink on a page without anyone choosing.
 *
 * It steps once per spoke rather than sweeping. A continuous rotation of a
 * radially symmetric mark reads as a smear at small sizes; twelve discrete
 * steps keep each spoke legible, which is how a mechanical seconds hand
 * works and why this glyph is drawn as spokes rather than as an arc.
 */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Turn = styled.span<{ $slow?: boolean }>`
  display: inline-flex;
  flex: none;
  line-height: 0;
  animation: ${spin} 1s steps(12, end) infinite;

  /**
   * Under prefers-reduced-motion the mark stays and only the spin slows.
   *
   * Stopping it altogether would leave a static glyph claiming something is
   * happening; a slower turn still says "working" without the flicker a fast
   * repeating animation causes for a reader who asked for less of it.
   */
  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }

  ${({ $slow }) =>
    $slow &&
    css`
      animation-duration: 2s;
    `}
`;

const Center = styled.div<{ $inset?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${({ $inset }) => $inset ?? 0}px 0;
`;

const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export type SpinnerProps = {
  /** Edge length in px. 14 in a button, 24 on a page. */
  size?: number;
  /** Overrides `currentColor`, for the rare indicator on a coloured ground. */
  color?: string;
  /** Announced while it turns. */
  label?: string;
  className?: string;
};

/** The mark on its own, for a button or an inline slot. */
export const Spinner: React.FC<SpinnerProps> = ({
  size = 14,
  color,
  label = "Loading",
  className,
}) => (
  <Turn className={className} role="status" style={color ? { color } : undefined}>
    <HugeIcon icon={Loading03Icon} size={size} strokeWidth={2} />
    <ScreenReaderOnly>{label}</ScreenReaderOnly>
  </Turn>
);

/**
 * The mark centred in whatever it is standing in for.
 *
 * A page, a card body, a table's place while it loads. Pages that load as
 * their own shape keep their skeletons — a block sized to the thing it
 * replaces tells a waiting reader more than a spinner does, and those are
 * deliberately untouched.
 */
export const SpinnerBlock: React.FC<SpinnerProps & { inset?: number }> = ({
  size = 24,
  inset = 24,
  ...rest
}) => (
  <Center $inset={inset}>
    <Spinner size={size} {...rest} />
  </Center>
);

export default Spinner;
